<script lang="ts">
	import { format } from 'svelte-i18n';
	import { copy } from 'src/helpers/copy_text';
	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
	import {
		Text,
		SidepanelContent,
		IconButton,
		Button,
		Modal,
		Container,
	} from '@Shelly/shelly-design-system';
	import ScriptsHighlight from 'src/components/DevicesSettings/Scripts/Components/ScriptsHighlight.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import SceneDeleteModal from 'src/components/Scenes/SceneDeleteModal.svelte';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import SharedHomeOwnerManagedAlert from 'src/components/SharedHome/SharedHomeOwnerManagedAlert.svelte';
	import { createEventDispatcher } from 'svelte';

	export let id;
	export let scene;
	// Accept extra props from parent to avoid Svelte warnings
	export let whens;
	export let thens;
	export let elses;
	export let active_time;
	export let run_on_save;
	export let scene_enable_val;
	export let allowed_for_3rd_party;
	export let duplicateInfo;

	const dispatch = createEventDispatcher();

	let showCodePreviewModal = false;
	let showDeleteSceneModal = false;

	$: isSharedHomeActive = isActiveHomeShared($homes.list);

	const onCloseCodePreviewModal = () => {
		showCodePreviewModal = false;
	};
</script>

<SidepanelContent>
	<Container backgroundColor="color-0" padding="xs">
		<Text styleType="label-large" class="text-center mb-s-plus">
			{$format('scene_id', { default: 'Scene id' })}
		</Text>
		<div class="d-flex justify-content-center align-items-center">
			<Text class="text-break-all text-center" styleType="body">{id}</Text>

			<IconButton
				aria-label={$format('button_copy_scene_id', {
					default: 'Button copy scene id',
				})}
				class="ml-s"
				icon="fas fa-copy"
				on:click={() =>
					copy(
						id,
						$format('copied_successfully', {
							default: 'Copied successfully!',
						}),
					)}
			/>
		</div>
		{#if isDevServerUser(['repo', 'nb']) || isT1User()}
			<Button
				class="mt-s-plus"
				width="full"
				size="m"
				variant="secondary"
				on:click={() => {
					showCodePreviewModal = true;
				}}
			>
				{$format('button_open_scene_code', {
					default: 'Open scene JSON',
				})}
			</Button>
		{/if}
	</Container>

	{#if isSharedHomeActive}
		<SharedHomeOwnerManagedAlert class="mt-s" context="delete_entity" />
	{/if}
	<Button
		class="mt-s"
		width="full"
		size="m"
		icon="fas fa-clone"
		disabled={isSharedHomeActive}
		aria-label={$format('new_button_duplicate_scene', {
			default: 'Duplicate scene',
		})}
		on:click={() => dispatch('duplicate')}
	>
		{$format('new_button_duplicate_scene', {
			default: 'Duplicate scene',
		})}
	</Button>

	<Button
		class="mt-s"
		width="full"
		size="m"
		variant="danger"
		icon="fas fa-trash"
		disabled={isSharedHomeActive}
		aria-label={$format('button_delete_scene', {
			default: 'Delete scene',
		})}
		on:click={() => (showDeleteSceneModal = true)}
	>
		{$format('button_delete_scene', {
			default: 'Delete scene',
		})}
	</Button>
</SidepanelContent>

<SceneDeleteModal bind:showDeleteSceneModal scene_id={id} scene_name={scene._meta.name} />

{#if isDevServerUser(['repo', 'nb']) || isT1User()}
	<Modal
		on:close={onCloseCodePreviewModal}
		showModal={showCodePreviewModal}
		title={$format('modal_title_scene_code', {
			default: 'Scene JSON',
		})}
	>
		<svelte:fragment slot="content">
			<Container hideSlotBottom hideSlotTop padding="0">
				<ScriptsHighlight code={JSON.stringify(scene, null, 2)} />
			</Container>
		</svelte:fragment>
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={(e) => (showCodePreviewModal = false)}
			>
				{capFirstChar($format('button_close', { default: 'Close' }))}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="primary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={(e) => {
					copy(
						JSON.stringify(scene, null, 2),
						$format('copied_successfully', {
							default: 'Copied successfully!',
						}),
					);
					showCodePreviewModal = false;
				}}
			>
				{capFirstChar($format('copy', { default: 'Copy' }))}
			</Button>
		</div>
	</Modal>
{/if}
